import React, { useEffect, useState, useRef, useContext } from 'react';
import { createSession } from './utils/jbSession';
import Localization from './utils/i18n';
import { prepareFormPayload } from './utils/prepareFormPayload';
import { requestCulture } from './actions/requestCulture';
import SelectEventSource from './components/SelectEventSource';
import { LocalizationProvider } from './context/LocalizationContext';
import { SelectedFormContext } from './context/SelectedFormContext';
import { NotificationContext } from './context/NotificationContext';

function App() {
	// Contexts
	const { formData } = useContext(SelectedFormContext);
	const { showInfoMessage } = useContext(NotificationContext);

	// Keep the latest formData in a ref so event handlers always read the current value
	const formDataRef = useRef(formData);
	formDataRef.current = formData;

	// States
	const [i18n, setI18n] = useState(null);
	const [appInitialized, setAppInitialized] = useState(false);

	// Refs
	const initEventPayload = useRef(null);
	const i18nInstance = useRef(null); // Create a ref for this so we can use this in our event handlers

	/**
	 * Setup event handling for JB window events.
	 * Protocol reference: https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/using-postmonger.html
	 */
	useEffect(() => {
		const connection = createSession();

		/**
		 * Handle the initEvent event sent from JB
		 */
		connection.on('initEvent', (payload) => {
			initEventPayload.current = payload;
			connection.trigger('ready');
		});

		/**
		 * Handle the next button being clicked in the JB Wizard
		 */
		connection.on('clickedNext', () => {
			// This is where we can validate the selected SC Form and send a
			// updateEvent message with the required data which will cause this plugin to close

			const payload = prepareFormPayload(formDataRef.current, initEventPayload.current);
			if (!payload) {
				showInfoMessage(i18nInstance.current.get('ALERT_NO_FORM_SELECTED'));
				connection.trigger('ready');
			} else {
				connection.trigger('updateEvent', payload);
			}
		});

		/**
		 * Handle the previous button being clicked in the JB Wizard
		 */
		connection.on('clickedBack', () => {
			connection.trigger('prevStep');
		});

		/**
		 * Handle the gotoStep event from JB
		 */
		connection.on('gotoStep', (step) => {
			// We'll render the app once we get the gotoStep event from JB. Since we only have one
			// step we can just render our app.
			setAppInitialized(true);
			connection.trigger('ready');
		});

		/**
		 * Initialize localization
		 */
		const initLocalization = async () => {
			const locale = await requestCulture();
			i18nInstance.current = Localization({ locale: locale, fallbackLocale: 'en-US' });
			await i18nInstance.current.load();
			setI18n(i18nInstance.current);

			/**
			 * Send the ready event to JB after localization is setup
			 */
			connection.trigger('ready');
		};
		initLocalization();

		return () => {
			// Cleanup code
			connection.off('initEvent');
			connection.off('clickedNext');
			connection.off('clickedBack');
			connection.off('gotoStep');
			connection.destroy();
		};
	}, []);

	if (i18n != null && appInitialized) {
		return (
			<LocalizationProvider i18n={i18n}>
				<SelectEventSource />
			</LocalizationProvider>
		);
	}
	return (
		<div />
	);
}

export default App;
